import { DEFAULT_LOCALE, LOCALES, type Locale } from '@sjuni/types';

/**
 * The per-locale map an admin field carries, both ways along the wire.
 *
 * docs/04-API-SPEC.md §6 — "Admin payloads send all locales at once", and the
 * admin resources return them the same way. So the form's value for a
 * translatable field is this shape from load to save, and nothing in between
 * has to remember which locale is on screen.
 */
export type TranslatedValue = Partial<Record<Locale, string | null>>;

/*
 * A locale left out of the payload is *kept*, not cleared.
 *
 * spatie's translatable merges a partial map into the stored one, so
 * `{ ka: 'x' }` updates Georgian and leaves whatever English was there. That is
 * the safe default for a partial API call, and it is a trap for a form: the way
 * to remove a translation is to send the key as an empty string, never to omit
 * it.
 *
 * The components here always carry the whole map, so an editor who clears the
 * English field sends `en: ''` and it clears. A module that builds its payload
 * by hand has to do the same.
 */

/** Georgian, per CLAUDE.md rule 1 and §6's "Georgian (`ka`) is required". */
export const REQUIRED_LOCALE: Locale = DEFAULT_LOCALE;

export function valueFor(value: TranslatedValue | null | undefined, locale: Locale): string {
  return value?.[locale] ?? '';
}

/**
 * Whether a locale has content.
 *
 * Whitespace does not count. An editor who tabs into the English field, types a
 * space and tabs out has not translated anything, and a completeness bar that
 * says otherwise is worse than no bar — CLAUDE.md rule 5 exists because the
 * current site's English is exactly this kind of almost-there.
 */
export function hasContent(value: TranslatedValue | null | undefined, locale: Locale): boolean {
  return (value?.[locale] ?? '').trim() !== '';
}

/** The locales still empty, in the order they appear in the toggle. */
export function missingLocales(value: TranslatedValue | null | undefined): Locale[] {
  return LOCALES.filter((locale) => !hasContent(value, locale));
}

/**
 * §4.1's "Copy KA → EN button for fields that are legitimately identical
 * (codes, names, URLs)".
 *
 * It copies rather than links. A field that stayed in sync would quietly
 * overwrite a real translation the moment someone fixed a typo in the Georgian,
 * and the editor would have no way to tell it had happened.
 */
export function copyLocale(
  value: TranslatedValue | null | undefined,
  from: Locale,
  to: Locale,
): TranslatedValue {
  return { ...(value ?? {}), [to]: value?.[from] ?? '' };
}

export function setLocale(
  value: TranslatedValue | null | undefined,
  locale: Locale,
  next: string,
): TranslatedValue {
  return { ...(value ?? {}), [locale]: next };
}

/**
 * §4.1's completeness bar — "EN 60% complete — 3 fields missing".
 *
 * Computed in the browser from the form's current values rather than read from
 * the resource's `translation_completeness`, because the bar has to move as the
 * editor types. The server's figure is the one that counts once saved; this is
 * the same arithmetic over unsaved state.
 */
export interface Completeness {
  locale: Locale;
  filled: number;
  total: number;
  /** 0–1. */
  ratio: number;
  missing: string[];
}

export function completenessFor(
  values: Record<string, TranslatedValue | null | undefined>,
  locale: Locale,
): Completeness {
  const fields = Object.keys(values);
  const missing = fields.filter((field) => !hasContent(values[field], locale));
  const filled = fields.length - missing.length;

  return {
    locale,
    filled,
    total: fields.length,
    // A record with no translatable fields is complete, not divided by zero.
    ratio: fields.length === 0 ? 1 : filled / fields.length,
    missing,
  };
}

/**
 * The saved shape for a translatable *list* — `partners`,
 * `required_documents`.
 *
 * Blank lines dropped, and a locale with nothing left removed rather than sent
 * as an empty array. An empty array is not the same as an absent one to
 * `translations_available`: it would count the locale as translated and light
 * up an EN badge for a list nobody has written.
 *
 * Here rather than beside `TranslatableListField` so that file exports only a
 * component — a module exporting both cannot be hot-reloaded, and every edit to
 * it would remount the tree and take the editor's unsaved work with it.
 */
export function listToInput(
  value: Record<string, string[] | null>,
): Record<string, string[] | null> {
  const cleaned: Record<string, string[] | null> = {};

  for (const [locale, items] of Object.entries(value)) {
    const kept = (items ?? []).map((item) => item.trim()).filter((item) => item !== '');

    cleaned[locale] = kept.length > 0 ? kept : null;
  }

  return cleaned;
}
